// src/ProfileVetting.js
//
// What the console could not tell you about a person.
//
// Audit §34 and §35 listed six gaps: a stylist's services, her availability,
// reports filed against her, her cancellation rate, and on the client side
// reports and somewhere to write a note. Approving a stylist without being
// able to see what she is selling or when she says she is free is approving on
// faith, and that is what the Stylist Approval screen was asking an admin to do.
//
// Everything shown is derived from live rows by public.admin_profile_vetting,
// so none of it can go stale. Loaded on demand rather than with the profile:
// it is several aggregates, and most visits to a profile are not a vetting.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Button, Chip,
  CircularProgress, Grid, TextField, Typography, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ink, surface, brand, type as typeScale } from './designTokens';

import { supabase } from './supabaseClient';
const Stat = ({ label, value, tone }) => (
  <Box>
    <Typography sx={{ color: ink[500], fontSize: typeScale.micro, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ color: tone || ink[900], fontSize: 20, fontWeight: 600, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export default function ProfileVetting({ phoneNumber, userType }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('Your session has expired. Sign in again.');
      const res = await fetch(`/api/profile-vetting?phone=${encodeURIComponent(phoneNumber)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setData(body.vetting);
      setNotes(body.vetting?.admin_notes || '');
      setSavedNotes(body.vetting?.admin_notes || '');
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoaded(true);
    }
  }, [phoneNumber]);

  useEffect(() => { if (loaded) load(); }, [loaded, load]);

  const saveNotes = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('Your session has expired. Sign in again.');
      const res = await fetch('/api/profile-vetting', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: phoneNumber, notes }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setSavedNotes(notes);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isStylist = String(userType || '').toLowerCase() === 'stylist';
  // A rate with no appointments behind it is not zero, it is no answer, and the
  // view returns null to say so.
  const rate = data?.cancellation_rate;

  return (
    <Accordion
      sx={{ mb: 1, border: `1px solid ${surface.line}`, borderRadius: 2, '&:before': { display: 'none' } }}
      onChange={(_, expanded) => { if (expanded && !loaded) setLoaded(true); }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
          Vetting
        </Typography>
        {data?.reports_against_open > 0 && (
          <Chip
            size="small"
            label={`${data.reports_against_open} open report${data.reports_against_open === 1 ? '' : 's'}`}
            sx={{ ml: 1.5, bgcolor: '#fdecea', color: '#9f3a38' }}
          />
        )}
      </AccordionSummary>

      <AccordionDetails>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loaded || (data === null && !error) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ color: brand.gold }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 2 }}>
              {isStylist && (
                <>
                  <Grid item xs={6} sm={3}>
                    <Stat label="Services" value={`${data?.services_active ?? 0} of ${data?.services ?? 0} active`} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Stat
                      label="Availability"
                      value={(data?.days_available ?? 0) === 0 ? 'None set' : `${data.days_available}d · ${data.hours_per_week}h/wk`}
                      tone={(data?.days_available ?? 0) === 0 ? ink[500] : undefined}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={6} sm={3}>
                <Stat label="Appointments" value={`${data?.completed ?? 0} of ${data?.appointments ?? 0} completed`} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stat
                  label="Cancellation rate"
                  value={rate === null || rate === undefined ? '—' : `${rate}%`}
                  tone={rate >= 20 ? '#9f3a38' : undefined}
                />
              </Grid>
              {isStylist && (
                <Grid item xs={6} sm={3}>
                  <Stat label="No-shows" value={data?.no_shows ?? 0} tone={(data?.no_shows ?? 0) > 0 ? '#9f3a38' : undefined} />
                </Grid>
              )}
              <Grid item xs={6} sm={3}>
                <Stat
                  label="Reports against"
                  value={data?.reports_against ?? 0}
                  tone={(data?.reports_against ?? 0) > 0 ? '#9f3a38' : undefined}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stat label="Reports filed" value={data?.reports_filed ?? 0} />
              </Grid>
            </Grid>

            {data?.last_report_at && (
              <Typography sx={{ color: ink[500], fontSize: typeScale.small, mb: 2 }}>
                Most recent report against this person: {data.last_report_type || 'unspecified'} on{' '}
                {new Date(data.last_report_at).toLocaleDateString()}. The Reports Queue has the detail.
              </Typography>
            )}

            {isStylist && data?.service_titles && (
              <Typography sx={{ color: ink[700], fontSize: typeScale.small, mb: 2 }}>
                <strong>Selling:</strong> {data.service_titles}
                {data.price_from != null && (
                  <> · {Number(data.price_from).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                    {Number(data.price_to) !== Number(data.price_from) && (
                      <>–{Number(data.price_to).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</>
                    )}
                  </>
                )}
              </Typography>
            )}

            {/* Profiles.admin_notes existed and nothing ever wrote to it. */}
            <TextField
              label="Admin notes"
              helperText="Only admins see this. Saving is recorded in the activity log."
              multiline
              minRows={2}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={saving || notes === savedNotes}
              onClick={saveNotes}
            >
              {saving ? 'Saving…' : notes === savedNotes ? 'Saved' : 'Save note'}
            </Button>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
